import { Store } from 'vuex';

import { AppState } from '@/store';
import { MealBuilderV2ModuleState, MUTATIONS as MEAL_BUILDER_V2_MUTATIONS } from '@/store/meal-builder-v2';

import { Logger } from '../logger/logger';
import { PromiseCacher } from '@/helpers/async';
import {
  MBV2SelectedItem,
  MBV2Error,
  ItemCustomizationId,
  OrderType,
  OrderMealRequest,
  BasicOrderMealItem,
  OrderMeal,
  MBV2NutritionSummary,
  MBV2NutritionSummaryDetails,
  MonoMenuTileItem,
  RecentOrFavoriteEntree,
  PreConfiguredMeal,
  MonoMenuCacheItem
} from '@/models';
import { MonoMenuService } from '../mono-menu/mono-menu.service';
import { UiService } from '../ui/ui.service';
import { OrderService } from '../order/order.service';
import { emptyNutritionData, EntreeCustomizationId, linkedSidesMap, utilityItemKeys, linkedNonVeggieToVeggieMap, linkedVeggieToNonVeggieMap } from '../mono-menu/mono-menu-constants';
import { CustomerService } from '../customer/customer.service';
import { MenuService } from '../menu/menu.service';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { isDefined, slugify } from '@/helpers';
import VueRouter from 'vue-router';
import { Routes } from '@/router';
import { addRequiredEntreeContents, adjustTacosMulitplier } from './meal-builder-v2-rules';
import { TemporaryContentGroups } from './meal-builder-v2-constants';
import { getNutritionException, isVeggieEntree } from '../mono-menu/mono-menu-rules';

export interface MealBuilderV2Service {
  openEmptyTopLevelMenuItem(title: string): Promise<void | MBV2Error>;
  upsertEntree(itemId: string, item: MBV2SelectedItem | null): Promise<void | MBV2Error>;
  removeEntree(itemId: string): Promise<void | MBV2Error>;
  upsertItem(itemId: string, item: MBV2SelectedItem | null): Promise<void | MBV2Error>;
  removeItem(itemId: string): Promise<void | MBV2Error>;
  switchToLinkedEntreeType(): void;
  addMealToBag(prebuiltMealState?: MealBuilderV2ModuleState): Promise<OrderMealRequest | MBV2Error>;
  getLifestyleBowlsTiles(): MonoMenuTileItem[];
  upsertPCE(itemId: string): void;
  removePCE(itemId: string): void;
  loadLifestyleBowlToState(mealId: string): void;
  addLifestyleBowlToBag(): Promise<PreConfiguredMeal | MBV2Error>;
  getRecentMealNutrition(entree: RecentOrFavoriteEntree): MBV2NutritionSummary;
  loadRecentMealToState(entree: RecentOrFavoriteEntree): MealBuilderV2ModuleState;
  addRecentMealToBag(entree: RecentOrFavoriteEntree): Promise<OrderMealRequest | MBV2Error>;
  getItemNutritionMultiplier(itemId: string | undefined, state: MealBuilderV2ModuleState | null): number;
  getCurrentMealDescription(): string;
  removeInvalidItems(prebuiltMealState?: MealBuilderV2ModuleState): Promise<any>;
  addDefaultContentForEntree(entreeId: string): void;
  selectDefaultContent(itemId: string): Promise<void | MBV2Error>;
  removeDefaultContent(itemId: string): Promise<void | MBV2Error>;
}

export class AppMealBuilderV2Service implements MealBuilderV2Service {
  constructor(
    private store: Store<AppState>,
    private monoMenu: MonoMenuService,
    private ui: UiService,
    private order: OrderService,
    private customer: CustomerService,
    private menu: MenuService,
    private router: VueRouter,
    private logger: Logger,
    private promiseCacher: PromiseCacher,
  ) {}

  MBState = this.store.state.mealBuilderV2;

  openEmptyTopLevelMenuItem(title: string) {
    const topLevelMenu = this.monoMenu.getTopLevelMenuByTitle(title);
    if (!topLevelMenu) {
      return Promise.reject({ errorText: 'DEV_ERROR: Only send valid TopLevelMenu titles' });
    }

    // clear everything
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.CLEAR_FULL_STATE);
    // set top level menu item
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_TOP_LEVEL_MENU_ITEM, title);
    // set the entree to default for the group
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ENTREE_ID, topLevelMenu.defaultEntreeId);
    // update nutrition to include defaults items
    
    this.updateNutritionState();

    if (topLevelMenu.defaultEntreeId) {
      this.addDefaultContentForEntree(topLevelMenu.defaultEntreeId);
    }

    return Promise.resolve();
  }

  removeEntree(entreeId: string) {
    if (this.store.state.mealBuilderV2.selectedItems[entreeId] === undefined) {
      return Promise.reject({ errorText: 'DEV_ERROR: only call with and entreeId is already in selectedItems' });
    }

    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SELECTED_ITEM, entreeId);

    const MBState = this.store.state.mealBuilderV2;
    // look of another entree and normalize the state
    const anotherEntreeId = Object.keys(MBState.selectedItems).find(itemId => itemId !== entreeId && this.monoMenu.getMonoMenuEntree(itemId) !== undefined);

    if (!anotherEntreeId) {
      // if none: return displayed entreeId to default
      const selectedTLM = this.monoMenu.getTopLevelMenuByTitle(MBState.topLevelMenuItemTitle!, true);
      if (MBState.entreeId != selectedTLM.defaultEntreeId) {
        const entree = this.monoMenu.getMonoMenuEntree(entreeId);
        if (entree?.entreeOptionTitle === 'One Taco' || entree?.entreeOptionTitle === `Kid’s Quesadilla`) {
          const defaultEntree = this.monoMenu.getMonoMenuEntree(selectedTLM.defaultEntreeId!);
          this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ENTREE_ID, defaultEntree?.linkedEntreeId);
        } else {
          this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ENTREE_ID, selectedTLM.defaultEntreeId);
        }

        
        // If removing a veggie protein, switch selected veggie specific items over to non veggie specific items
        if (isVeggieEntree(entree)) {
          this.switchLinkedItems(linkedVeggieToNonVeggieMap)
        }
      }
    } else {
      // if one: update quantity to match customization
      const selectedEntree = MBState.selectedItems[anotherEntreeId];
      selectedEntree.quantity = selectedEntree.customization === EntreeCustomizationId.Double ? 2 : null;
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, selectedEntree);
    }
    
    this.clearItemError(entreeId);
    this.updateNutritionState();
    return Promise.resolve();
  }

  // call without 'item' to add in default state
  upsertEntree(entreeId: string, item: MBV2SelectedItem | null = null): Promise<void | MBV2Error> {
    const monoMenuEntree = this.monoMenu.getMonoMenuEntree(entreeId);
    if (monoMenuEntree === undefined) {
      return Promise.reject({ errorText: 'DEV_ERROR: this function is only for valid entrees' });
    }

    // validate the customization
    if (item?.customization != null && monoMenuEntree.customizations?.find(c => c.id === item?.customization) === undefined) {
      return Promise.reject({ errorText: 'DEV_ERROR: item.customization must be a valid customization id' });
    }

    const MBState = this.store.state.mealBuilderV2;
    const thisEntreeIsAlreadyAdded = MBState.selectedItems[entreeId] !== undefined;
    const otherEntreeIds = this.getSelectedEntreeIds().filter(id => id !== entreeId);

    if (otherEntreeIds.length >= 2) {
      return this.setAndClearItemError(entreeId, 'You can only select 2 fillings');
    }

    const anotherEntreeExists = otherEntreeIds.length !== 0;

    if (anotherEntreeExists && MBState.topLevelMenuItemTitle === 'Tacos') {
      // tacos dont have split protein, just overwrite the existing selection
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, {
        itemId: entreeId,
        customization: MBState.selectedItems[otherEntreeIds[0]]?.customization || null,
        quantity: MBState.selectedItems[otherEntreeIds[0]]?.customization === EntreeCustomizationId.Double ? 2 : null,
      });
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SELECTED_ITEM, otherEntreeIds[0]);
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ENTREE_ID, entreeId);
      this.updateNutritionState();
      return Promise.resolve();
    }

    if (anotherEntreeExists) {
      // check to make sure the other entree will play well with others
      const otherEntree = this.monoMenu.getMonoMenuEntree(otherEntreeIds[0])!;
      if (otherEntree.rules.maxExtras === 0 || monoMenuEntree.rules.maxHalfs === 0) {
        const notEnoughHalfsEntreeName = monoMenuEntree.rules.maxHalfs === 0 ? monoMenuEntree.primaryFillingName : otherEntree.primaryFillingName;
        if (item?.customization === EntreeCustomizationId.Double) {
          return this.setAndClearItemError(entreeId, `Can't go double with ${notEnoughHalfsEntreeName}`);
        } else {
          return this.setAndClearItemError(entreeId, `Can't go halfsies with ${notEnoughHalfsEntreeName}`);
        }
      }

      // update other entree customization + quantity
      const selectedEntree = MBState.selectedItems[otherEntreeIds[0]];

      // For customizations we want to clear on null or keep the double customization on empty input or EntreeCustomizationId.Double input
      // If we ever add any other customizations this will be wrong
      selectedEntree.customization = item?.customization === null ? null : item?.customization !== undefined ? item?.customization : selectedEntree.customization;
      selectedEntree.quantity = (item?.customization || selectedEntree.customization) === EntreeCustomizationId.Double ? 1 : 0.5;
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, selectedEntree);

      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, {
        itemId: entreeId,
        customization: selectedEntree.customization,
        quantity: selectedEntree.quantity,
      });
    } else {
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, {
        itemId: entreeId,
        customization: item?.customization || null,
        quantity: item?.customization === EntreeCustomizationId.Double ? 2 : null,
      });

      // If selecting a veggie protein, switch selected non veggie specific items over to veggie specific items
      if (isVeggieEntree(monoMenuEntree)) {
        this.switchLinkedItems(linkedNonVeggieToVeggieMap)
      }
    }

    if (!thisEntreeIsAlreadyAdded && !anotherEntreeExists) {
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ENTREE_ID, entreeId);
      this.clearSectionError('EntreeContentGroup');
    }

    this.updateNutritionState();
    this.addDefaultContentForEntree(entreeId);

    return Promise.resolve();
  }

  removeItem(itemId: string) {
    // if the item is a half, remove customization and quantity from the other item in the group
    const selectedItem = this.store.state.mealBuilderV2.selectedItems[itemId];

    if (!selectedItem) {
      return Promise.reject({ errorText: `DEV_ERROR: item ${itemId} was not selected` });
    }

    if (selectedItem.customization === ItemCustomizationId.Half) {
      const otherContentGroupItems = this.filterSelectedItems(
        i => i !== itemId && this.monoMenu.getMonoMenuItem(itemId)?.rules.contentGroupName === this.monoMenu.getMonoMenuItem(i)?.rules.contentGroupName,
      );
      otherContentGroupItems.forEach(otherItemId => {
        if (this.store.state.mealBuilderV2.selectedItems[itemId].customization === ItemCustomizationId.Half) {
          this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, {
            itemId: otherItemId,
            customization: null,
            quantity: null,
          });
        }
      });
    }

    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SELECTED_ITEM, itemId);
    this.clearItemError(itemId);
    this.updateNutritionState();
    return Promise.resolve();
  }

  upsertItem(itemId: string, item: MBV2SelectedItem | null): Promise<void | MBV2Error> {
    const getItem = (id: string) => this.monoMenu.getMonoMenuItem(id);
    const monoMenuItem = getItem(itemId);
    if (!monoMenuItem) {
      return Promise.reject({ errorText: `DEV_ERROR: item not found in menu ${itemId}` });
    }

    // validate that we can add this item to the entree
    const MBState = this.store.state.mealBuilderV2;
    const referenceEntree = this.monoMenu.getMonoMenuEntree(
      Object.keys(MBState.selectedItems).find(id => this.monoMenu.getMonoMenuEntree(id) !== undefined) || this.store.state.mealBuilderV2.entreeId!,
    )!;

    if (referenceEntree.allowedItems.indexOf(itemId) === -1) {
      return Promise.reject({
        errorText: `DEV_ERROR: ${monoMenuItem?.itemName} is not allowed with entree ${this.store.state.mealBuilderV2.entreeId}`,
      });
    }

    // validate the customization - halfs cannot be manually assigned
    if (item?.customization === ItemCustomizationId.Half || (item?.customization != null && !this.itemHasCustomization(itemId, item?.customization))) {
      return Promise.reject({
        errorText: `DEV_ERROR: ${item?.customization} is not a valid customization for ${itemId}`,
      });
    }

    if (!monoMenuItem.rules.maxQuantity && item?.quantity && item?.quantity != 0.5) {
      return Promise.reject({
        errorText: `DEV_ERROR: ${itemId} does not support quantity`,
      });
    }

    if (item?.quantity && monoMenuItem.rules.maxQuantity! < item?.quantity) {
      return this.setAndClearItemError(itemId, `Only ${monoMenuItem.rules.maxQuantity} ${monoMenuItem.itemName} allowed`);
    }

    const newSelectedItem = {
      itemId,
      customization: item?.customization || null,
      quantity: item?.quantity || null,
    };

    // Check on the side customizations before we look at the content group rules, since on the side customizations span all content groups for the whole entree
    const currentItemOnTheSideCustomizationValue = monoMenuItem.customizations?.find(c => c.id === newSelectedItem.customization)?.countTowardsOnTheSideCustomizationMax;
    const sumOfOtherItemOnTheSideCustomizationValues = Object.keys(MBState.selectedItems)
      .map(id => getItem(id)?.customizations?.find(c => c.id === MBState.selectedItems[id].customization)?.countTowardsOnTheSideCustomizationMax || 0)
      .reduce((partial_sum, a) => partial_sum + a, 0);
    const entreeOnTheSideCustomizationMax = this.monoMenu.getMonoMenuEntree(MBState.entreeId!)?.rules?.maxOnTheSideCustomizations;
    if (
      entreeOnTheSideCustomizationMax &&
      entreeOnTheSideCustomizationMax !== -1 &&
      newSelectedItem.customization !== null &&
      currentItemOnTheSideCustomizationValue &&
      currentItemOnTheSideCustomizationValue + sumOfOtherItemOnTheSideCustomizationValues > entreeOnTheSideCustomizationMax
    ) {
      return this.setAndClearItemError(itemId, `Max of ${entreeOnTheSideCustomizationMax} included sides`);
    }

    const hasContentGroup =
      monoMenuItem.rules.contentGroupName &&
      this.monoMenu.getMonoMenuEntree(MBState.entreeId!)!.groups.find(cg => cg.contentGroupName === monoMenuItem.rules.contentGroupName);
    if (!hasContentGroup) {
      // set the item for the simple case
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, newSelectedItem);
    } else {
      // find the against the other content group items
      const otherContentGroupItems = this.filterSelectedItems(i => i !== itemId && monoMenuItem.rules.contentGroupName === getItem(i)?.rules.contentGroupName);

      // if no other items
      if (otherContentGroupItems.length === 0) {
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, newSelectedItem);
        this.updateNutritionState();

        if (hasContentGroup) {
          this.clearSectionError(monoMenuItem.rules.contentGroupName);
        }

        return Promise.resolve();
      }

      // the new or existing item is a NO_* utility item
      if (getItem(otherContentGroupItems[0])?.itemType === 'Empty' || monoMenuItem.itemType === 'Empty') {
        // remove all otherContentGroupItems
        otherContentGroupItems.forEach(itemId => this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SELECTED_ITEM, itemId));

        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, newSelectedItem);
        this.updateNutritionState();
        return Promise.resolve();
      }

      // if only one other item exists && both items are allowed half customomizations
      if (
        otherContentGroupItems.length === 1 &&
        this.itemHasCustomization(itemId, ItemCustomizationId.Half) &&
        this.itemHasCustomization(otherContentGroupItems[0], ItemCustomizationId.Half)
      ) {
        // change the customizations of both to half
        const otherItem = MBState.selectedItems[otherContentGroupItems[0]];
        otherItem.customization = ItemCustomizationId.Half;
        otherItem.quantity = 0.5;
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, otherItem);

        // upsert new item
        newSelectedItem.customization = ItemCustomizationId.Half;
        newSelectedItem.quantity = 0.5;
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, newSelectedItem);
        if (item?.customization) {
          return this.setAndClearItemError(itemId, `No customizations left for ${this.monoMenu.getItemGroupTitle(referenceEntree, itemId) || monoMenuItem.itemType}`);
        }
        this.updateNutritionState();
        return Promise.resolve();
      }

      // since other items exist, validate the CustomizationMax
      // if the new item has a customization and sum(customization value) > the max for the entree
      const currentItemCustomizationValue = monoMenuItem.customizations?.find(c => c.id === newSelectedItem.customization)?.countTowardsCustomizationMax;
      const sumOfOtherItemCustomizationValues = otherContentGroupItems
        .map(id => getItem(id)?.customizations?.find(c => c.id === MBState.selectedItems[id].customization)?.countTowardsCustomizationMax || 0)
        .reduce((partial_sum, a) => partial_sum + a, 0);
      const entreeCustomizationMax = this.monoMenu.getMonoMenuEntree(MBState.entreeId!)!.rules.maxCustomizations;
      if (
        entreeCustomizationMax !== -1 &&
        newSelectedItem.customization !== null &&
        currentItemCustomizationValue &&
        currentItemCustomizationValue + sumOfOtherItemCustomizationValues > entreeCustomizationMax
      ) {
        return this.setAndClearItemError(itemId, `No customizations left for ${this.monoMenu.getItemGroupTitle(referenceEntree, itemId) || monoMenuItem.itemType}`);
      }

      // since other items exist, validate the ContentMax
      const contentGroup = this.monoMenu.getMonoMenuEntree(MBState.entreeId!)!.groups.find(cg => cg.contentGroupName === monoMenuItem.rules.contentGroupName)!;

      const totalQuantity = [newSelectedItem, ...otherContentGroupItems.map(id => MBState.selectedItems[id])]
        .map(
          selectedItem =>
            (getItem(selectedItem?.itemId)?.rules.countTowardsContentMax || 0) +
            (monoMenuItem.customizations?.find(cust => cust.id === selectedItem.customization)?.countTowardsContentMax || 0),
        )
        .reduce((partial_sum, a) => partial_sum + a, 0);

      if (contentGroup.maxQuantity && totalQuantity > contentGroup.maxQuantity) {
          return this.setAndClearItemError(itemId, `You can choose up to ${contentGroup.maxQuantity} ${this.monoMenu.getItemGroupTitle(referenceEntree, itemId) || monoMenuItem.itemType}`);
      }

      // Catchall for if all validations and special cases passed. Most of the time one of the special cases above will have already triggered
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, newSelectedItem);
    }

    this.updateNutritionState();
    return Promise.resolve();
  }

  upsertPCE(itemId: string) {
    // if not already selected, clear all other selections and select
    if (!this.store.state.mealBuilderV2.selectedItems[itemId]) {
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.CLEAR_SELECTED_ITEMS);
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, {
        itemId,
        customization: null,
        quantity: null,
      });
      this.updateNutritionStatePCE();
    }
  }

  removePCE(itemId: string) {
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SELECTED_ITEM, itemId);
    this.updateNutritionState();
  }

  filterSelectedItems(filterFn: (itemId: string) => Boolean, prebuiltMealState?: MealBuilderV2ModuleState) {
    return Object.keys(prebuiltMealState?.selectedItems || this.store.state.mealBuilderV2.selectedItems).filter(i => filterFn(i));
  }

  findSelectedItem(finderFn: (itemId: string) => Boolean, prebuiltMealState?: MealBuilderV2ModuleState) {
    return Object.keys(prebuiltMealState?.selectedItems || this.store.state.mealBuilderV2.selectedItems).find(i => finderFn(i));
  }

  getSelectedEntreeIds(prebuiltMealState?: MealBuilderV2ModuleState) {
    return Object.keys(prebuiltMealState?.selectedItems || this.MBState.selectedItems).filter(id => this.monoMenu.getMonoMenuEntree(id) !== undefined);
  }

  itemHasCustomization(id: string, customization: number) {
    return !!this.monoMenu.getMonoMenuItem(id)?.customizations?.find(c => c.id === customization);
  }

  getSelectedItemsInContentGroup(contentGroupName: string) {
    return this.filterSelectedItems(i => contentGroupName === this.monoMenu.getMonoMenuItem(i)?.rules.contentGroupName);
  }

  setAndClearItemError(itemId: string, errorText: string): Promise<void | MBV2Error> {
    this.setItemError(itemId, errorText);
    this.clearItemErrorOnTimeout(itemId);
    return Promise.reject({ itemId, errorText });
  }

  clearItemErrorOnTimeout(itemId: string) {
    setTimeout(() => {
      this.clearItemError(itemId);
    }, 3000);
  }

  setItemError(itemId: string, errorText: string) {
    const error = { itemId, errorText };
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ITEM_ERROR, error);
  }

  clearItemError(itemId: string) {
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_ITEM_ERROR, itemId);
  }

  setAndClearSectionError(contentGroupName: string | undefined, errorText: string): Promise<void | MBV2Error> {
    this.setSectionError(contentGroupName, errorText);
    this.clearSectionErrorOnTimeout(contentGroupName);
    return Promise.reject({ contentGroupName, errorText });
  }

  clearSectionErrorOnTimeout(contentGroupName: string | undefined) {
    setTimeout(() => {
      this.clearSectionError(contentGroupName);
    }, 3000);
  }

  setSectionError(contentGroupName: string | undefined, errorText: string) {
    const error = { contentGroupName: contentGroupName, errorText: errorText };
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SECTION_ERROR, error);
  }

  clearSectionError(contentGroupName: string | undefined) {
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SECTION_ERROR, contentGroupName);
  }

  setAndClearOrderButtonError(errorText: string): Promise<void | MBV2Error> {
    this.setOrderButtonError(errorText);
    this.clearOrderButtonErrorOnTimeout();
    return Promise.reject({ errorText });
  }

  clearOrderButtonErrorOnTimeout() {
    setTimeout(() => {
      this.clearOrderButtonError();
    }, 3000);
  }

  setOrderButtonError(errorText: string) {
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ORDER_BUTTON_ERROR, errorText)
  }

  clearOrderButtonError() {
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_ORDER_BUTTON_ERROR);
  }

  switchToLinkedEntreeType() {
    // for kids meals and tacos
    const MBState = this.store.state.mealBuilderV2;
    if (!MBState.entreeId) {
      return Promise.reject({ errorText: 'DEV_ERROR: No entree is currently being displayed' });
    }
    const currentEntree = this.monoMenu.getMonoMenuEntree(MBState.entreeId!)!;
    // for kids meals we clear the items and go to the other meal types entree default
    if (MBState.topLevelMenuItemTitle === `Kid's Meal`) {
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.CLEAR_SELECTED_ITEMS);
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ENTREE_ID, currentEntree.linkedEntreeId);
    }

    // for tacos we change the menu entreeId, if an entree selection has been made remove the old entree and add the new one
    if (MBState.topLevelMenuItemTitle === `Tacos`) {
      const selectedEntree = this.findSelectedItem(i => i === MBState.entreeId);
      if (selectedEntree) {
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, {
          itemId: this.monoMenu.getMonoMenuEntree(selectedEntree)!.linkedEntreeId,
          customization: null,
          quantity: null,
        });
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SELECTED_ITEM, selectedEntree);
      }
      this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_ENTREE_ID, currentEntree.linkedEntreeId);

      this.switchLinkedItems(linkedSidesMap);
    }

    this.updateNutritionState();
  }

  async addMealToBag(prebuiltMealState?: MealBuilderV2ModuleState): Promise<OrderMealRequest | MBV2Error> {
    const MBState = prebuiltMealState || this.store.state.mealBuilderV2;

    try {
      if (!this.store.state.restaurant.selectedRestaurant) {
        await this.findAChipotle();
        await this.monoMenu.awaitCacheItemsAndBuildMenu();
      }

      await this.removeInvalidItems(MBState);
      await this.validateMeal(prebuiltMealState);

      if (!MBState.mealName) {
        return this.getNewMealName(false, prebuiltMealState);
      } else {
        return this.updateMeal(prebuiltMealState);
      }
    } catch (err) {
      if ((err as MBV2Error)?.errorText) {
        // TODO test with non-mealbuilder errors
        this.setAndClearOrderButtonError((err as MBV2Error).errorText);
        return Promise.reject(err);
      }
      throw err;
    }
  }

  async updateMeal(prebuiltMealState?: MealBuilderV2ModuleState) {
    const MBState = prebuiltMealState || this.store.state.mealBuilderV2;
    const packagedMeal = this.packageMBV2StateIntoMeal(prebuiltMealState);

    if (!this.store.state.customer.jwt) {
      await this.customer.createAutogeneratedUser();
    }
    const order = this.order.getCurrentOrder() || (await this.order.createOrder(this.store.state.restaurant.selectedRestaurant!.restaurantNumber, OrderType.Regular))!;
    if (!MBState.mealId) {
      await this.order.addMealToOrder(order, packagedMeal);
    } else {
      await this.order.updateMealInOrder(order, MBState.mealId, packagedMeal);
    }
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.CLEAR_FULL_STATE);

    return Promise.resolve(packagedMeal);
  }

  // - validate whole meal
  validateMeal(prebuiltMealState?: MealBuilderV2ModuleState): Promise<any> {
    // for each content group in the main entree, validate the group: min quantity, max quantity and hasSelectNoneOption
    const MBState = prebuiltMealState || this.store.state.mealBuilderV2;
    const selectedEntree = this.monoMenu.getMonoMenuEntree(MBState.entreeId!)!;

    const missingSections = [];
    if (!this.findSelectedItem(i => i === MBState.entreeId!, prebuiltMealState) && MBState.entreeId !== utilityItemKeys.sidesEntree) {
      missingSections.push('protein or veggie');
      this.setAndClearSectionError('EntreeContentGroup', 'Please choose your filling');
    }

    // for each content group in the main entree, validate the group: min quantity, max quantity and hasSelectNoneOption
    selectedEntree.groups.forEach(group => {
      if (group.type === 'item') {
        // maxQuantity is checked on item add so just check minQuantity and hasSelectNoneOption
        const itemsInGroup = this.filterSelectedItems(
          i => group.contentGroupName === this.monoMenu.getMonoMenuItem(i, true, prebuiltMealState?.entreeId || undefined)?.rules.contentGroupName,
          prebuiltMealState,
        );
        let totalGroupQuantity = 0;
        itemsInGroup.forEach(itemId => {
          const item = this.monoMenu.getMonoMenuItem(itemId, true, prebuiltMealState?.entreeId || undefined)!;

          totalGroupQuantity +=
            (item.rules.countTowardsContentMax || 0) +
            (item.customizations?.find(cust => cust.id === MBState.selectedItems[itemId].customization)?.countTowardsContentMax || 0);
        });

        if ((group.minQuantity && totalGroupQuantity < group.minQuantity) || (group.hasSelectNoneOption && itemsInGroup.length === 0)) {
          missingSections.push(group.title.toLowerCase());
          this.setAndClearSectionError(group.contentGroupName, `Please choose your ${group.title.toLowerCase()}`);
        }
      }
    });

    if (missingSections.length !== 0) {
      if (missingSections.length >= 2) {
        missingSections[missingSections.length - 1] = `and ${missingSections[missingSections.length - 1]}`;
      }
      return Promise.reject({ errorText: `Please choose your ${missingSections.join(missingSections.length > 2 ? ', ' : ' ')}` });
    }

    // If we've gotten this far and no items have been selected, we're in a special top level menu. At least one item needs to be selected.
    if (Object.keys(MBState.selectedItems).length === 0) {
      return Promise.reject({ errorText: `Please choose an item` });
    }

    return Promise.resolve(true);
  }

  async messageErrorAndEditMeal(message: string, prebuiltMealState: MealBuilderV2ModuleState): Promise<any> {
    return new Promise((resolve, reject) => {
      const actionAfterClick = () => {
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_FULL_STATE, prebuiltMealState);
        this.updateNutritionState();
        this.router.push({ name: Routes.NutritionCalculcatorMealBuilder, params: { topLevelName: slugify(prebuiltMealState.topLevelMenuItemTitle!) } });
        reject({ errorText: message });
      };

      this.ui.setModalVisibility('basicError', true, {
        header: 'OH DANG.',
        content: message,
        options: [
          {
            text: 'Okay',
            type: ButtonType.Primary,
            onClick: () => {
              this.ui.setModalVisibility('basicError', false);
              actionAfterClick();
            },
          },
        ],
      });
    });
  }

  async messageErrorAndAbortMealAdd(message: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const actionAfterClick = () => {
        this.router.push({ name: Routes.NutritionCalculatorPage });
        reject({ errorText: 'Start a new meal to try again' });
      };

      this.ui.setModalVisibility('basicError', true, {
        header: 'OH DANG.',
        content: message,
        options: [
          {
            text: 'Okay',
            type: ButtonType.Primary,
            onClick: () => {
              this.ui.setModalVisibility('basicError', false);
              actionAfterClick();
            },
          },
        ],
      });
    });
  }

  async removeInvalidItems(prebuiltMealState?: MealBuilderV2ModuleState): Promise<any> {
    // Remove unavailable items and items not in the current resturant's menu

    const MBState = prebuiltMealState || this.store.state.mealBuilderV2;

    const selectedTLM = this.monoMenu.getTopLevelMenuByTitle(MBState.topLevelMenuItemTitle!, true);
    if (!selectedTLM) {
      return this.messageErrorAndAbortMealAdd('Sorry, this entree type is unavailable at the current resturant');
    }

    // if anything in there is invalid, blind remove it, fix the meal state after
    const currentItemIds = Object.keys(MBState.selectedItems);
    const startingItemCount = currentItemIds.length;
    let foundUnavailable = false;
    currentItemIds.forEach(itemId => {
      if (!this.monoMenu.isItemInMenu(itemId)) {
        delete MBState.selectedItems[itemId];
      }
    });

    // validate the whole meal now that we've removed item
    // entree validations: entree count 1 or 2, entree customizations and quantities match
    const entreeIds = Object.keys(MBState.selectedItems).filter(itemId => this.monoMenu.getMonoMenuEntree(itemId) !== undefined);

    // catch completely invalid entree states
    if (entreeIds.length === 0 || entreeIds.length > 2 || (entreeIds.length > 1 && MBState.topLevelMenuItemTitle === 'Tacos')) {
      if (entreeIds.length > 2) {
        entreeIds.forEach(id => {
          delete MBState.selectedItems[id];
        });
      }
      MBState.entreeId = selectedTLM.defaultEntreeId!;
    }

    // if we removed the primary entree set a new one
    MBState.entreeId = entreeIds[0] || selectedTLM.defaultEntreeId!;
    if (entreeIds.length === 1) {
      MBState.selectedItems[entreeIds[0]].quantity = MBState.selectedItems[entreeIds[0]].customization === EntreeCustomizationId.Double ? 2 : null;
    }
    // for 2 entrees, assume we didnt delete anything

    // item validations:
    const selectedEntree = this.monoMenu.getMonoMenuEntree(MBState.entreeId!)!;
    const itemIds = Object.keys(MBState.selectedItems)
      .filter(itemId => !entreeIds.includes(itemId)) // only items
      .filter(itemId => {
        // remove items not allowed for this entree
        const itemIsAllowed = selectedEntree.allowedItems.includes(itemId);
        if (!itemIsAllowed) {
          delete MBState.selectedItems[itemId];
        }
        return itemIsAllowed;
      });

    // remove customizations that are not allowed, this would only trigger if the entree rules changed since this entree was built
    itemIds.forEach(itemId => {
      const item = this.monoMenu.getMonoMenuItem(itemId, true, MBState.entreeId!)!;
      if (!item.customizations?.find(customization => customization.id === MBState.selectedItems[itemId].customization)) {
        MBState.selectedItems[itemId].customization = null;
        MBState.selectedItems[itemId].quantity = null;
      }
    });

    // for each content group in the main entree, remove lonely halves
    selectedEntree.groups.forEach(group => {
      if (group.type === 'item') {
        const itemsInGroup = this.filterSelectedItems(
          i => group.contentGroupName === this.monoMenu.getMonoMenuItem(i, true, MBState.entreeId!)?.rules.contentGroupName,
          prebuiltMealState,
        );

        // no lonely halfs
        if (itemsInGroup.length === 1 && MBState.selectedItems[itemsInGroup[0]].customization === ItemCustomizationId.Half) {
          MBState.selectedItems[itemsInGroup[0]].customization = null;
          MBState.selectedItems[itemsInGroup[0]].quantity = null;
        }
      }
    });

    if (startingItemCount !== Object.keys(MBState.selectedItems).length) {
      return this.messageErrorAndEditMeal(
        'Some items are unavailable at the current restaurant and were removed from your meal. Check to make sure it still looks good without them.',
        MBState,
      );
    }

    // Soft reject for unavailable items
    Object.keys(MBState.selectedItems).forEach(itemId => {
      if (
        (this.monoMenu.getMonoMenuEntree(itemId) && this.monoMenu.getMonoMenuEntree(itemId)?.rules.isItemAvailable === false) ||
        (this.monoMenu.getMonoMenuItem(itemId, true, MBState.entreeId || selectedTLM.defaultEntreeId!) &&
          this.monoMenu.getMonoMenuItem(itemId, true, MBState.entreeId || selectedTLM.defaultEntreeId!)?.rules.isItemAvailable === false)
      ) {
        this.setAndClearItemError(itemId, 'Update selection to add to bag');
        foundUnavailable = true;
      }
    });
    if (foundUnavailable) {
      return this.messageErrorAndEditMeal('An item in your meal is temporarily unavailable. Remove it or choose something else to order your meal.', MBState);
    }

    return Promise.resolve(true);
  }

  async getNewMealName(isPce: boolean, prebuiltMealState?: MealBuilderV2ModuleState): Promise<any | void> {
    return new Promise((resolve, reject) => {
      let preFilledMealName = this.store.state.customer.profile?.firstName || '';

      const whenModelIsFinished = async (mealName: string) => {
        if (!mealName) {
          reject();
          return Promise.reject(false);
        }

        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_MEAL_NAME, mealName);

        if (isPce) {
          resolve(await this.updatePCE());
        } else {
          resolve(await this.updateMeal(prebuiltMealState));
        }
      };

      this.ui.setModalVisibility('mealBuilderMealName', true, {
        beforeClose: mealName => whenModelIsFinished(mealName),
        mealName: preFilledMealName,
      });
      this.ui.firstFocus();
    });
  }

  findAChipotle(): Promise<any | void> {
    return new Promise((resolve, reject) => {
      const whenModelIsFinished = () => {
        if (!this.store.state.restaurant.selectedRestaurant) {
          reject({ errorText: 'Select a resturant to continue' });
        }
        resolve(true);
      };

      this.ui.setModalVisibility('findAChipotle', true, {
        onClose: () => whenModelIsFinished(),
        suppressPickupDeliveryToast: true,
      });
    });
  }

  // - state to meal
  packageMBV2StateIntoMeal(prebuiltMealState?: MealBuilderV2ModuleState): OrderMealRequest {
    // skipping validation steps here. only call this function if you've called the other validators
    const MBState = prebuiltMealState || this.MBState;

    const orderEntreeIds = this.getSelectedEntreeIds(prebuiltMealState);

    const entree = this.monoMenu.getMonoMenuEntree(orderEntreeIds[0])!;

    const sidesAndDrinks = this.monoMenu.getMonoMenuEntree(utilityItemKeys.sidesEntree)!;
    const entreeContents: BasicOrderMealItem[] = [];
    const orderSides: BasicOrderMealItem[] = [];
    const orderDrinks: BasicOrderMealItem[] = [];

    if (entree && entree.itemId !== utilityItemKeys.sidesEntree) {
      // half or double protein
      if (MBState.selectedItems[entree.itemId].customization === EntreeCustomizationId.Double) {
        entreeContents.push({
          isUpSell: false,
          menuItemId:
            orderEntreeIds.length > 1 ? this.monoMenu.getMonoMenuEntreeExtraPortion(orderEntreeIds[1]) : this.monoMenu.getMonoMenuEntreeExtraPortion(orderEntreeIds[0]), // extra self or extra of another protein
          menuItemName: this.monoMenu.getMonoMenuEntree(orderEntreeIds.length > 1 ? orderEntreeIds[1] : orderEntreeIds[0])?.primaryFillingName || '',
          quantity: 1,
        });
      } else {
        if (orderEntreeIds.length > 1) {
          entreeContents.push({
            isUpSell: false,
            menuItemId: this.monoMenu.getMonoMenuEntreeHalfPortion(orderEntreeIds[1]),
            menuItemName: this.monoMenu.getMonoMenuEntree(orderEntreeIds[1])?.primaryFillingName || '',
            quantity: 1,
          });
        }
      }
    }

    // split the remaining items out into their types
    Object.keys(MBState.selectedItems).forEach(itemId => {
      const item = this.monoMenu.getMonoMenuItem(itemId, true, prebuiltMealState?.entreeId || undefined);
      if (item && item.itemType !== 'Empty') {
        // if its not a side or a drink
        if (sidesAndDrinks.allowedItems.indexOf(itemId) === -1) {
          const packagedItem: BasicOrderMealItem = {
            isUpSell: false,
            menuItemId: itemId,
            menuItemName: item.itemName,
            quantity: Math.max(MBState.selectedItems[itemId].quantity || 1, 1),
          };
          if (MBState.selectedItems[itemId].customization) {
            packagedItem.customizationId = MBState.selectedItems[itemId].customization!;
          }

          entreeContents.push(packagedItem);
        } else {
          // if its a drink
          if (sidesAndDrinks.groups.find(i => i.contentGroupName === 'Drink')?.items.indexOf(itemId) !== -1) {
            orderDrinks.push({
              isUpSell: false,
              menuItemId: itemId,
              menuItemName: item.itemName,
              quantity: MBState.selectedItems[itemId].quantity || 1,
            });
          } else {
            orderSides.push({
              isUpSell: false,
              menuItemId: itemId,
              menuItemName: item.itemName,
              quantity: MBState.selectedItems[itemId].quantity || 1,
            });
          }
        }
      }
    });

    if (this.MBState.entreeId !== utilityItemKeys.sidesEntree) {
      addRequiredEntreeContents(this.monoMenu, entree, entreeContents);
    }

    return {
      meal: {
        mealName: this.store.state.mealBuilderV2.mealName!,
        entrees:
          this.MBState.entreeId !== utilityItemKeys.sidesEntree
            ? [
                {
                  menuItemId: MBState.entreeId!,
                  menuItemName: '',
                  quantity: 1,
                  contents: entreeContents,
                },
              ]
            : [],
        sides: orderSides,
        drinks: orderDrinks,
      },
    };
  }

  mealToMBV2State(meal: Partial<OrderMeal>): MealBuilderV2ModuleState {
    // TODO add check to make sure the items in the meal are in the selected menu
    const selectedItems = {} as { [index: string]: MBV2SelectedItem };
    if (meal.entrees![0]) {
      selectedItems[meal.entrees![0].menuItemId! || (meal.entrees![0] as any).itemId] = {
        itemId: meal.entrees![0].menuItemId || (meal.entrees![0] as any).itemId,
        customization: null,
        quantity: null,
      };

      meal.entrees![0].contents?.forEach(item => {
        const monoMenuItem = this.monoMenu.getMonoMenuItem(item.menuItemId || (item as any).itemId, true, meal.entrees![0].menuItemId || (meal.entrees![0] as any).itemId);
        if (monoMenuItem?.itemType === 'HalfPortion') {
          selectedItems[meal.entrees![0].menuItemId].quantity = 0.5;
          selectedItems[monoMenuItem.rules.pricingReferenceItemId || item.menuItemId || (item as any).itemId] = {
            itemId: monoMenuItem.rules.pricingReferenceItemId || item.menuItemId || (item as any).itemId,
            customization: null,
            quantity: 0.5,
          };
        } else if (monoMenuItem?.itemType === 'ExtraPortion') {
          if (monoMenuItem.rules.pricingReferenceItemId !== meal.entrees![0].menuItemId) {
            selectedItems[meal.entrees![0].menuItemId].quantity = 1;
            selectedItems[meal.entrees![0].menuItemId].customization = EntreeCustomizationId.Double;
            // MBv2 uses the original CMG ID for extra portions and then converts it after for COP (i.e. CMG-2 instead of CMG-1102)
            selectedItems[monoMenuItem.rules.pricingReferenceItemId || item.menuItemId || (item as any).itemId] = {
              itemId: monoMenuItem.rules.pricingReferenceItemId || item.menuItemId || (item as any).itemId,
              customization: EntreeCustomizationId.Double,
              quantity: 1,
            };
          } else {
            selectedItems[meal.entrees![0].menuItemId].quantity = 2;
            selectedItems[meal.entrees![0].menuItemId].customization = EntreeCustomizationId.Double;
          }
        } else {
          selectedItems[item.menuItemId || (item as any).itemId] = {
            itemId: item.menuItemId || (item as any).itemId,
            customization: item.customizationId || null,
            quantity: item.quantity !== 1 ? item.quantity : null,
          };
        }
      });
    }
    meal.sides?.forEach(item => {
      selectedItems[item.menuItemId || (item as any).itemId] = {
        itemId: item.menuItemId || (item as any).itemId,
        customization: item.customizationId || null,
        quantity: item.quantity !== 1 ? item.quantity : null,
      };
    });

    meal.drinks?.forEach(item => {
      selectedItems[item.menuItemId || (item as any).itemId] = {
        itemId: item.menuItemId || (item as any).itemId,
        customization: item.customizationId || null,
        quantity: item.quantity !== 1 ? item.quantity : null,
      };
    });

    const entreeItem = this.monoMenu.getMonoMenuEntree(meal.entrees![0].menuItemId)!;

    entreeItem.groups.forEach(group => {
      // group.hasSelectNoneOption && selected items overlap is 0
      if (group.hasSelectNoneOption && group.items.filter(value => Object.keys(selectedItems).includes(value)).length === 0) {
        selectedItems[group.items[group.items.length - 1]] = {
          itemId: group.items[group.items.length - 1],
          customization: null,
          quantity: null,
        };
      }
    });

    return {
      topLevelMenuItemTitle: meal.entrees![0] ? this.monoMenu.getTopLevelMenuByTitle(entreeItem.itemType).title : 'Sides & Drinks',
      entreeId: meal.entrees![0] ? meal.entrees![0].menuItemId : utilityItemKeys.sidesEntree,
      selectedItems,
      mealId: meal.mealId || null,
      mealName: meal.mealName || null,
      itemErrors: {},
      sectionErrors: {},
      orderButtonError: null,
      nutrition: Object.assign({}, emptyNutritionData),
      optedOutDefaultItemIds: []
    };
  }

  nutritionDataFromMBv2State(state: MealBuilderV2ModuleState, details: boolean = false): MBV2NutritionSummary | MBV2NutritionSummaryDetails {
    const nutritionKeys = ['tcal', 'tfat', 'prot', 'carb'];
    const nutritionDetailsKeys = ['tcal', 'calf', 'tfat', 'satu', 'tran', 'prot', 'carb', 'fibe', 'suga', 'sodi', 'vita', 'vitc', 'iron', 'calc'];

    const nutritionValues = details
      ? Object.assign({}, emptyNutritionData)
      : {
          tcal: 0,
          tfat: 0,
          prot: 0,
          carb: 0,
        };

    
    Object.keys(state.selectedItems).forEach(itemId => {
      let multiple = this.getItemNutritionMultiplier(itemId, state);
      let nutrition:{ [index: string]: number };

      const nutritionException = getNutritionException(itemId, state.topLevelMenuItemTitle);
      if (nutritionException) {
        nutrition = nutritionException.nutrition as { [index: string]: number };
      } else {
        nutrition = (this.monoMenu.getMonoMenuItem(itemId, true, state.entreeId!) || this.monoMenu.getMonoMenuEntree(itemId))?.nutrition as { [index: string]: number };
      }
      if (nutrition) {
        ((details ? nutritionDetailsKeys : nutritionKeys) as (keyof typeof nutritionValues)[]).forEach(key => {
          nutritionValues[key] += (nutrition[key] || 0) * multiple;
        });
      }
    });

    if (state.entreeId && this.monoMenu.getMonoMenuEntree(state.entreeId)) {
      const includedItemsGroup = this.monoMenu.getMonoMenuEntree(state.entreeId)?.groups[0];
      if (includedItemsGroup!.type === 'includedItem') {
        includedItemsGroup!.items.forEach(itemId => {
          const nutrition = this.monoMenu.getMonoMenuItem(itemId, true, state.entreeId!)?.nutrition;
          if (nutrition) {
            ((details ? nutritionDetailsKeys : nutritionKeys) as (keyof typeof nutritionValues)[]).forEach(key => {
              nutritionValues[key] += nutrition[key] || 0;
            });
          }
        });
      }
    }
    return nutritionValues;
  }

  updateNutritionState() {
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_NUTRITION, this.nutritionDataFromMBv2State(this.store.state.mealBuilderV2, true));
  }

  updateNutritionStatePCE() {
    // this.lifestyleBowlToState
    this.store.commit(
      MEAL_BUILDER_V2_MUTATIONS.SET_NUTRITION,
      this.nutritionDataFromMBv2State(this.lifestyleBowlToState(Object.keys(this.store.state.mealBuilderV2.selectedItems)[0]), true),
    );
  }

  nutritionDataFromCOPEntree(meal: OrderMeal, details: boolean = false): MBV2NutritionSummary | MBV2NutritionSummaryDetails {
    return this.nutritionDataFromMBv2State(this.mealToMBV2State(meal), details);
  }

  getLifestyleBowlsTiles(): MonoMenuTileItem[] {
    const lifestyleBowls = this.store.state.menu.preConfiguredMeals.filter(pce => pce.mealType === 'Lifestyle' && this.validatePCEContents(pce));

    lifestyleBowls.forEach(lb => {
      const mealShim = ({
        entrees: [lb.entree],
        sides: lb.sides,
        drinks: lb.drinks,
      } as any) as OrderMeal;
      mealShim.entrees![0].menuItemId = lb.entree.itemId;
      lb.nutrition = this.nutritionDataFromCOPEntree(mealShim, false);
    });

    return lifestyleBowls.map(lb => {
      return {
        itemType: 'PCE',
        itemName: lb.mealName,
        itemId: lb.mealId,
        unitPrice: lb.mealPrice,
        unitDeliveryPrice: lb.mealDeliveryPrice,
        // thumbnailUrl: lb.primaryImages.find(i=> i.imageCategory === 'WEB')?.imageUrl!,
        nutrition: lb.nutrition!,
        tags: [
          {
            type: 'tagline',
            value: lb.entree.ingredientsSummary,
          },
        ],
      };
    });
  }

  loadLifestyleBowlToState(mealId: string) {
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_FULL_STATE, this.lifestyleBowlToState(mealId));
    this.updateNutritionState();
  }

  lifestyleBowlToState(mealId: string) {
    // get meal
    const meal = this.store.state.menu.preConfiguredMeals.find(pce => pce.mealId === mealId)!;

    // convert to state
    const mealShim = ({
      entrees: [meal.entree],
      sides: meal.sides,
      drinks: meal.drinks,
    } as any) as OrderMeal;
    
    return this.mealToMBV2State(mealShim);
  }

  async addLifestyleBowlToBag(): Promise<PreConfiguredMeal | MBV2Error> {
    const MBState = this.store.state.mealBuilderV2;
    try {
      if (!this.store.state.restaurant.selectedRestaurant) {
        await this.findAChipotle();
      }

      const preConfiguredMeal = await this.validatePCESelection();
      if (!MBState.mealName) {
        await this.getNewMealName(true, MBState);
      } else {
        await this.updatePCE();
      }
      return Promise.resolve(preConfiguredMeal);
    } catch (err) {
      if ((err as MBV2Error)?.errorText) {
        // TODO test with non-mealbuilder errors
        this.setAndClearOrderButtonError((err as MBV2Error).errorText);
        return Promise.reject(err);
      }
      throw err;
    }
  }

  async updatePCE() {
    const MBState = this.store.state.mealBuilderV2;

    if (!this.store.state.customer.jwt) {
      await this.customer.createAutogeneratedUser();
    }
    const order = this.order.getCurrentOrder() || (await this.order.createOrder(this.store.state.restaurant.selectedRestaurant!.restaurantNumber, OrderType.Regular))!;
    await this.order.addPreconfiguredMealToOrder(order, Object.keys(MBState.selectedItems)[0], MBState.mealName!);
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.CLEAR_FULL_STATE);
  }

  // let's make sure the contents of the PCE are on the menu
  validatePCEContents(preConfiguredMeal: PreConfiguredMeal): boolean {
    if (!this.monoMenu.isItemInMenu(preConfiguredMeal.entree.itemId)) {
      return false;
    }

    const invalidEntreeContent = preConfiguredMeal.entree.contents.find(c => !this.monoMenu.isItemInMenu(c.itemId));
    const invalidDrink = preConfiguredMeal.drinks.find(d => !this.monoMenu.isItemInMenu(d.itemId));
    const invalidSide = preConfiguredMeal.sides.find(s => !this.monoMenu.isItemInMenu(s.itemId));

    return !(invalidEntreeContent || invalidDrink || invalidSide);
  }

  validatePCESelection(): Promise<PreConfiguredMeal | MBV2Error> {
    const MBState = this.store.state.mealBuilderV2;
    // make sure there's only one selection
    if (Object.keys(MBState.selectedItems).length === 0) {
      return Promise.reject({ errorText: `Please choose an Entree` });
    }
    if (Object.keys(MBState.selectedItems).length > 1) {
      return Promise.reject({ errorText: `Only one Entree Allowed at a time` });
    }

    // make sure its a valid PCE
    const PCEs = this.store.state.menu.preConfiguredMeals;
    const matchingPCE = PCEs.find(pce => pce.mealId === Object.keys(MBState.selectedItems)[0]);
    if (!matchingPCE) {
      return Promise.reject({ errorText: `DEV ERROR: Not a valid PCE ID` });
    }
    return Promise.resolve(matchingPCE);
  }

  recentMealToMBV2State(entree: RecentOrFavoriteEntree) {
    const mbv2State = this.mealToMBV2State({ entrees: [entree as any] });
    return mbv2State;
  }

  async addRecentMealToBag(entree: RecentOrFavoriteEntree) {
    return this.addMealToBag(this.recentMealToMBV2State(entree));
  }

  getRecentMealNutrition(entree: RecentOrFavoriteEntree): MBV2NutritionSummary {
    return this.nutritionDataFromMBv2State(this.recentMealToMBV2State(entree), false);
  }

  loadRecentMealToState(entree: RecentOrFavoriteEntree): MealBuilderV2ModuleState {
    const mbv2State = this.recentMealToMBV2State(entree);

    // push to state
    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_FULL_STATE, mbv2State);
    this.updateNutritionState();
    return mbv2State;
  }

  getItemNutritionMultiplier(itemId: string | undefined, state: MealBuilderV2ModuleState | null): number {
    let multiplier = 1;
    state = state || this.store.state.mealBuilderV2
    const tileState = state.selectedItems[itemId || ''];
    const topLevelMenuItemTitle = state.topLevelMenuItemTitle;

    if (!!tileState) {
      if (tileState.customization === ItemCustomizationId.Extra) {
        multiplier = 2;
      }
      if (tileState.customization === EntreeCustomizationId.Double && tileState.quantity === 2) {
        multiplier = 2;
      }
      if (tileState.customization === ItemCustomizationId.Light) {
        multiplier = 0.5;
      }
      if (tileState.customization === ItemCustomizationId.Half) {
        multiplier = 0.5;
      }
      if (!tileState.customization && tileState.quantity === 0.5) {
        multiplier = 0.5;
      }
    }

    multiplier = adjustTacosMulitplier(this.monoMenu, itemId, topLevelMenuItemTitle, state.entreeId, multiplier);

    return multiplier;
  }

  getCurrentMealDescription(): string {
    const selectedEntreeIds = this.getSelectedEntreeIds();
    const selectedSideIds = this.getSelectedItemsInContentGroup(TemporaryContentGroups.SideContentGroup);
    const selectedDrinkIds = this.getSelectedItemsInContentGroup(TemporaryContentGroups.DrinkContentGroup);
    const selectedItemIds = Object.keys(this.store.state.mealBuilderV2.selectedItems);
    const menuTitle = this.store.state.mealBuilderV2.topLevelMenuItemTitle;

    if (selectedEntreeIds.length) {
      let entreeDescription = '';

      for (const selectedItemId of selectedEntreeIds) {
        const entree = this.monoMenu.getMonoMenuEntree(selectedItemId);

        if (!entreeDescription) {
          entreeDescription = entree!.primaryFillingName;
        } else {
          entreeDescription += ' & ' + entree!.primaryFillingName;
        }
      }

      const sideDescription = selectedSideIds.length + (selectedSideIds.length > 1 ? ' Sides' : ' Side');
      const drinkDescription = selectedDrinkIds.length + (selectedDrinkIds.length > 1 ? ' Drinks' : ' Drink');

      return entreeDescription + ' ' + menuTitle + (selectedSideIds.length ? ', ' + sideDescription : '') + (selectedDrinkIds.length ? ', ' + drinkDescription : '');
    } else if (menuTitle === 'Sides & Drinks' && (selectedSideIds.length || selectedDrinkIds.length)) {
      const sideDescription = selectedSideIds.length + (selectedSideIds.length > 1 ? ' Sides' : ' Side');
      const drinkDescription = selectedDrinkIds.length + (selectedDrinkIds.length > 1 ? ' Drinks' : ' Drink');

      return (
        (selectedSideIds.length ? sideDescription : '') + (selectedSideIds.length && selectedDrinkIds.length ? ', ' : '') + (selectedDrinkIds.length ? drinkDescription : '')
      );
    } else if (menuTitle === 'Lifestyle Bowl' && selectedItemIds.length) {
      return this.store.state.menu.preConfiguredMeals.find(m => m.mealId === selectedItemIds[0])?.mealName || '';
    }

    return 'Select ' + (menuTitle === 'Sides & Drinks' || menuTitle === 'Lifestyle Bowl' ? 'an item' : 'a protein or veggie') + ' to get started';
  }

  switchLinkedItems(itemMap: { [index: string]: string | undefined }){
      // Unfortunately, the CMG Ids for Guac and Queso in regards to One Taco vs Three Tacos are different
      // When the customer switches between 1 and 3 tacos, switch the CMG IDs over. For now, we'll use a one-off constant - linkedSidesMap
      // If this use case becomes more common, we can model this like linked entrees, and add a linkedSideId to mono menu items
    const MBState = this.store.state.mealBuilderV2;
    const currentItemIds = Object.keys(MBState.selectedItems);
    for (const itemId of currentItemIds) {
      const linkedItemId = itemMap[itemId];
      if (linkedItemId) {
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.REMOVE_SELECTED_ITEM, itemId);
        this.store.commit(MEAL_BUILDER_V2_MUTATIONS.SET_SELECTED_ITEM, {
          itemId: linkedItemId,
          customization: null,
          quantity: null,
        });
      }
    }
  }

  addDefaultContentForEntree(entreeId: string): void {
    const entree = this.monoMenu.getMonoMenuEntree(entreeId);

    entree?.groups.forEach(g => {
      let items = g.items.map(id => (this.monoMenu.getMonoMenuItem(id, true)) as MonoMenuTileItem).filter(this.monoMenu.isItemAvailable);
      items.forEach(i => {
        if (this.monoMenu.isDefaultItem(i)) {
          this.selectDefaultContent(i.itemId);
        }
      })
    });
  }

  selectDefaultContent(itemId: string): Promise<void | MBV2Error> {
    if (this.store.state.mealBuilderV2.optedOutDefaultItemIds.indexOf(itemId) === -1) {
      return this.upsertItem(itemId, null);
    } 
    
    return Promise.resolve();
  }

  removeDefaultContent(itemId: string): Promise<void | MBV2Error> {
    const selectedItem = this.store.state.mealBuilderV2.selectedItems[itemId];

    if (!selectedItem) {
      return Promise.reject({ errorText: `DEV_ERROR: item ${itemId} was not selected` });
    }

    this.store.commit(MEAL_BUILDER_V2_MUTATIONS.ADD_OPTED_OUT_DEFAULT_ITEM, itemId);
    return this.removeItem(itemId);
  }
}
